import React, { useState, useRef, useCallback, useMemo } from 'react';
import { DeepChat } from 'deep-chat-react';

// Renders feedback buttons using native DOM (not React) to work inside DeepChat's shadow DOM.
// Only one message shows buttons at a time — the most recent AI message.
// latestFeedbackHideRef: ref holding a hide function for the currently visible buttons.
//   When a new message's buttons are rendered, this hides the previous one's.
// reactionsRef: ref holding { [messageId]: 'like'|'dislike' } — persists across close/reopen.
function renderFeedbackButtons(container, sessionId, messageId, xsrfToken, latestFeedbackHideRef, reactionsRef) {
    // Hide buttons on whatever was previously the latest message.
    if (latestFeedbackHideRef.current) {
        latestFeedbackHideRef.current();
    }

    // Restore reaction given in this page session (survives close/reopen cycles).
    let feedback = reactionsRef.current[messageId] || null;

    let feedbackForm = null;

    const wrapper = document.createElement('div');
    wrapper.style.cssText = 'display:flex;gap:2px;justify-content:flex-end;align-items:center;';

    const makeBtn = (emoji, reaction) => {
        const btn = document.createElement('button');
        btn.innerHTML = emoji;
        btn.title = reaction === 'like' ? 'Helpful' : 'Not helpful';
        btn.style.cssText = 'background:none;border:none;cursor:pointer;padding:0 4px;font-size:14px;line-height:1;opacity:0.5;transition:opacity 0.15s ease;';
        btn.addEventListener('mouseenter', () => { if (!feedback) btn.style.opacity = '0.8'; });
        btn.addEventListener('mouseleave', () => { if (!feedback) btn.style.opacity = '0.5'; });
        btn.addEventListener('click', (e) => {
            e.stopPropagation();
            if (feedback === reaction) return;

            feedback = reaction;
            reactionsRef.current[messageId] = reaction;
            likeBtn.style.opacity = reaction === 'like' ? '1' : '0.5';
            likeBtn.style.cursor = 'pointer';
            dislikeBtn.style.opacity = reaction === 'dislike' ? '1' : '0.5';
            dislikeBtn.style.cursor = 'pointer';
            if (reaction === 'dislike') {
                showFeedbackForm();
            } else if (feedbackForm) {
                feedbackForm.remove();
                feedbackForm = null;
            }

            fetch(`/api/chat/${sessionId}/feedback/reaction`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'XSRF-TOKEN': xsrfToken },
                body: JSON.stringify({ reaction, messageId })
            }).catch(err => {
                console.error('Failed to send reaction:', err);
            });
        });
        return btn;
    };

    const likeBtn = makeBtn('\u{1F44D}', 'like');
    const dislikeBtn = makeBtn('\u{1F44E}', 'dislike');
    wrapper.appendChild(likeBtn);
    wrapper.appendChild(dislikeBtn);
    container.appendChild(wrapper);

    // Restore visual state for a previously given reaction.
    if (feedback) {
        likeBtn.style.opacity = feedback === 'like' ? '1' : '0.5';
        dislikeBtn.style.opacity = feedback === 'dislike' ? '1' : '0.5';
    }

    // Register this as the latest — the next message will call this to hide these buttons. It also
    // disables this message's reschedule slot buttons (if any), so only the newest message's slots
    // stay clickable.
    latestFeedbackHideRef.current = () => {
        wrapper.style.display = 'none';
        if (feedbackForm) { feedbackForm.remove(); feedbackForm = null; }
        const messageRoot = container.parentElement;
        const slotContainer = messageRoot ? messageRoot.querySelector('.chat-reschedule-container') : null;
        disableSlotContainer(slotContainer);
    };

    function showFeedbackForm() {
        if (feedbackForm) {
            feedbackForm.remove();
            feedbackForm = null;
        }

        const form = document.createElement('div');
        form.style.cssText = 'margin-top:6px;';
        feedbackForm = form;

        const textarea = document.createElement('textarea');
        textarea.placeholder = 'What could we have done better? (optional)';
        textarea.style.cssText = 'width:100%;min-height:60px;border-radius:6px;border:1px solid #ccc;padding:6px;font-size:12px;resize:vertical;box-sizing:border-box;';

        const btnRow = document.createElement('div');
        btnRow.style.cssText = 'display:flex;gap:6px;margin-top:4px;';

        const submitBtn = document.createElement('button');
        submitBtn.textContent = 'Submit';
        submitBtn.style.cssText = 'background-color:#c41e3a;color:white;border:none;border-radius:4px;padding:4px 12px;font-size:12px;cursor:pointer;';
        submitBtn.addEventListener('click', async (e) => {
            e.stopPropagation();
            if (!textarea.value.trim()) { form.remove(); feedbackForm = null; return; }
            submitBtn.textContent = 'Sending...';
            submitBtn.disabled = true;
            try {
                await fetch(`/api/chat/${sessionId}/feedback/negative`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json', 'XSRF-TOKEN': xsrfToken },
                    body: JSON.stringify({ messageId, feedbackText: textarea.value })
                });
            } catch (err) {
                console.error('Failed to send negative feedback:', err);
            }
            form.remove();
            feedbackForm = null;
        });

        const skipBtn = document.createElement('button');
        skipBtn.textContent = 'Skip';
        skipBtn.style.cssText = 'background-color:#eee;border:none;border-radius:4px;padding:4px 12px;font-size:12px;cursor:pointer;';
        skipBtn.addEventListener('click', (e) => { e.stopPropagation(); form.remove(); feedbackForm = null; });

        btnRow.appendChild(submitBtn);
        btnRow.appendChild(skipBtn);
        form.appendChild(textarea);
        form.appendChild(btnRow);
        container.appendChild(form);
    }
}

// Greys out and blocks interaction on a message's reschedule slot buttons. Called when a newer AI
// message arrives so the student can only book from the most recent set of options — older slots
// are stale (they may no longer be available) and shouldn't be clickable. Idempotent.
function disableSlotContainer(slotContainer) {
    if (!slotContainer || slotContainer.getAttribute('data-slots-disabled') === 'true') return;
    slotContainer.setAttribute('data-slots-disabled', 'true');
    slotContainer.style.opacity = '0.5';
    slotContainer.style.pointerEvents = 'none';
}

// Renders clickable reschedule slot buttons into an AI message (native DOM, like the feedback
// buttons, to work inside DeepChat's shadow DOM). Clicking a slot asks for a quick confirmation,
// then posts the slot's remoteMeetingId straight to /api/chat/reschedule — the booking never
// depends on the model re-deriving the id from chat text. `options` is { classNumber, type, slots:
// [{ remoteMeetingId, startDateTimeLocal, totalStudents }] }.
function renderSlotButtons(container, options, xsrfToken) {
    const slots = Array.isArray(options.slots) ? options.slots : [];
    if (slots.length === 0) return;

    const wrapper = document.createElement('div');
    wrapper.style.cssText = 'display:flex;flex-direction:column;gap:6px;margin-top:8px;';

    const showStatus = (text, color) => {
        wrapper.innerHTML = '';
        const status = document.createElement('div');
        status.textContent = text;
        status.style.cssText = `font-size:13px;font-weight:600;color:${color};padding:4px 0;`;
        wrapper.appendChild(status);
    };

    const book = (slot) => {
        showStatus('Booking…', '#555');
        fetch('/api/chat/reschedule', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'XSRF-TOKEN': xsrfToken },
            body: JSON.stringify({
                remoteMeetingId: slot.remoteMeetingId,
                classNumber: options.classNumber,
                type: options.type,
                slotLabel: slot.startDateTimeLocal
            })
        })
            .then(r => r.json())
            .then(data => {
                if (data && data.success) {
                    showStatus('✓ ' + (data.response || 'Your class was rescheduled.'), '#1a7f37');
                } else {
                    showStatus((data && (data.response || data.error)) || 'That time could not be booked. Please pick another or call (314) 788-3100.', '#c41e3a');
                }
            })
            .catch(err => {
                console.error('Failed to book slot:', err);
                showStatus('Something went wrong booking that time. Please try again or call (314) 788-3100.', '#c41e3a');
            });
    };

    const confirm = (slot) => {
        wrapper.innerHTML = '';
        const prompt = document.createElement('div');
        prompt.textContent = `Book ${slot.startDateTimeLocal}?`;
        prompt.style.cssText = 'font-size:13px;margin-bottom:6px;';

        const row = document.createElement('div');
        row.style.cssText = 'display:flex;gap:6px;';

        const yes = document.createElement('button');
        yes.textContent = 'Yes, book it';
        yes.style.cssText = 'background-color:#c41e3a;color:white;border:none;border-radius:6px;padding:6px 12px;font-size:13px;cursor:pointer;';
        yes.addEventListener('click', (e) => { e.stopPropagation(); book(slot); });

        const cancel = document.createElement('button');
        cancel.textContent = 'Cancel';
        cancel.style.cssText = 'background-color:#eee;border:none;border-radius:6px;padding:6px 12px;font-size:13px;cursor:pointer;';
        cancel.addEventListener('click', (e) => { e.stopPropagation(); renderButtons(); });

        row.appendChild(yes);
        row.appendChild(cancel);
        prompt.appendChild(row);
        wrapper.appendChild(prompt);
    };

    function renderButtons() {
        wrapper.innerHTML = '';
        slots.forEach(slot => {
            const btn = document.createElement('button');
            btn.textContent = slot.startDateTimeLocal;
            btn.style.cssText = 'text-align:left;background-color:#fff;color:#c41e3a;border:1px solid #c41e3a;border-radius:8px;padding:8px 12px;font-size:13px;cursor:pointer;transition:background-color 0.15s ease, color 0.15s ease;';
            // On hover, fill with the brand red so it's obvious which slot is about to be booked.
            btn.addEventListener('mouseenter', () => { btn.style.backgroundColor = '#c41e3a'; btn.style.color = '#fff'; });
            btn.addEventListener('mouseleave', () => { btn.style.backgroundColor = '#fff'; btn.style.color = '#c41e3a'; });
            btn.addEventListener('click', (e) => { e.stopPropagation(); confirm(slot); });
            wrapper.appendChild(btn);
        });
    }

    renderButtons();
    // Only now that the buttons built successfully, drop the no-JS text fallback and attach them.
    // Removing it after renderButtons() (not before) means a throw while building leaves the fallback
    // in place — the observer's catch just logs — so the student still sees the times, never an empty
    // "tap one below" bubble.
    const fallback = container.querySelector('.chat-reschedule-fallback');
    if (fallback) fallback.remove();
    container.appendChild(wrapper);
}

// Builds the reschedule-slot container HTML that the MutationObserver later fills with booking
// buttons. Shared by the live response path and the history-restore path so a refreshed session
// re-renders the exact same buttons. Returns '' when there are no slots to offer.
function buildRescheduleHtml(opts) {
    if (!opts || !Array.isArray(opts.slots) || opts.slots.length === 0) return '';
    const slots = opts.slots.map(s => ({
        remoteMeetingId: s.remoteMeetingId,
        startDateTimeLocal: s.startDateTimeLocal,
        totalStudents: s.totalStudents
    }));
    const payload = encodeURIComponent(JSON.stringify({
        classNumber: opts.classNumber,
        type: opts.type,
        slots
    }));
    // Plain-text fallback baked into the container so the times stay visible even if the JS that
    // upgrades them into tappable buttons never runs (observer miss, DeepChat re-render, history
    // restore). renderSlotButtons removes this before rendering the buttons, so on the happy path the
    // student only ever sees the buttons — no duplication — but a render hiccup degrades to a usable
    // numbered list instead of a dead "tap one below" with nothing to tap.
    const esc = (str) => String(str).replace(/[&<>"]/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));
    const fallbackList = slots.map((s, i) => `${i + 1}. ${esc(s.startDateTimeLocal)}`).join('<br>');
    return `<div class="chat-reschedule-container" data-options="${payload}" style="pointer-events:auto;position:relative;z-index:1;">`
        + `<div class="chat-reschedule-fallback" style="margin-top:8px;font-size:13px;">${fallbackList}</div>`
        + `</div>`;
}

// Static DeepChat props defined outside the component so their references are stable across
// re-renders. DeepChat re-renders message bubbles when prop references change, which wipes
// any feedback buttons injected directly into the DOM.
const HTML_CLASS_UTILITIES = {
    'chat-feedback-container': { events: { click: () => {}, mousedown: () => {} } },
    'chat-reschedule-container': { events: { click: () => {}, mousedown: () => {} } }
};

const MESSAGE_STYLES = {
    default: {
        shared: {
            bubble: {
                maxWidth: '85%',
                borderRadius: '12px',
                padding: '10px 14px'
            }
        },
        user: {
            bubble: {
                backgroundColor: '#c41e3a',
                color: 'white'
            }
        },
        ai: {
            bubble: {
                backgroundColor: '#f0f0f0',
                color: '#333'
            }
        }
    }
};

const INPUT_AREA_STYLE = { borderTop: '1px solid #e0e0e0' };

const SUBMIT_BUTTON_STYLES = {
    submit: {
        container: {
            default: { backgroundColor: '#c41e3a' },
            hover: { backgroundColor: '#a01830' }
        }
    }
};

const AVATARS = {
    ai: {
        src: '/images/jclogosmall.png',
        styles: {
            avatar: { width: '30px', height: '30px' }
        }
    }
};

const TEXT_INPUT = { placeholder: { text: 'Type your message...' } };

// Customer Service phone formats Vincent uses in assistant replies — linked for mobile tap-to-call.
const CS_PHONE_PATTERN = /(\(314\)\s*788-3100|314-788-3100)/g;
const CS_PHONE_TEL = 'tel:+13147883100';

function linkCustomerServicePhones(markdown) {
    return markdown.replace(CS_PHONE_PATTERN, (match) => `[${match}](${CS_PHONE_TEL})`);
}

function renderAssistantMarkdown(text) {
    return window.marked.parse(linkCustomerServicePhones(text), { gfm: true });
}

const DEEP_CHAT_STYLE = {
    height: '500px',
    width: '380px',
    borderRadius: '12px',
    border: 'none',
    backgroundColor: '#ffffff'
};

const CHAT_PANEL_STYLE = {
    position: 'fixed',
    bottom: '90px',
    right: '20px',
    width: '380px',
    height: '500px',
    borderRadius: '12px',
    boxShadow: '0 4px 20px rgba(0,0,0,0.25)',
    zIndex: 2147483647,
    overflow: 'hidden',
    backgroundColor: '#ffffff'
};

const TOGGLE_BUTTON_STYLE = {
    position: 'fixed',
    bottom: '20px',
    right: '20px',
    width: '60px',
    height: '60px',
    borderRadius: '50%',
    backgroundColor: '#c41e3a',
    border: 'none',
    cursor: 'pointer',
    boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
    zIndex: 2147483647,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'transform 0.2s ease, background-color 0.2s ease'
};

const ICON_STYLE = { color: 'white', fontSize: '28px' };

function onToggleButtonMouseEnter(e) {
    e.currentTarget.style.transform = 'scale(1.05)';
    e.currentTarget.style.backgroundColor = '#a01830';
}

function onToggleButtonMouseLeave(e) {
    e.currentTarget.style.transform = '';
    e.currentTarget.style.backgroundColor = '#c41e3a';
}

export const ChatAgentComponent = ({ accountNumber, firstName }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [initialMessages, setInitialMessages] = useState(undefined);
    const [chatKey, setChatKey] = useState(0);
    const sessionIdRef = useRef(null);
    const messageMetaRef = useRef([]);
    const historyFetchedRef = useRef(false);
    // Persists all messages ({role, text}[]) so they survive close/reopen cycles.
    // User messages are captured in requestInterceptor; AI messages in responseInterceptor.
    const conversationRef = useRef([]);
    // Holds the active MutationObserver cleanup so it can be disconnected on remount.
    const observerCleanupRef = useRef(null);
    // Holds a hide function for whichever message currently shows feedback buttons.
    // Replaced each time a new AI message is rendered; called to hide the previous one.
    const latestFeedbackHideRef = useRef(null);
    // Tracks reactions given this page session: { [messageId]: 'like' | 'dislike' }.
    // Persists across close/reopen cycles so buttons restore their selected state.
    const reactionsRef = useRef({});

    const xsrfToken = document.querySelector('input[name="__RequestVerificationToken"]')?.value || '';

    const introMessage = useMemo(() => ({
        text: `Hello ${firstName ? firstName + ',' : ''} what can I help you with today?`
    }), [firstName]);

    const toggleChat = () => {
        if (isOpen) {
            setIsOpen(false);
            return;
        }


        // Reopen after the user has already had a conversation: restore persisted messages
        // by reinitializing DeepChat with them as history. DeepChat does not reliably
        // maintain its internal message state through display:none/flex cycles.
        if (conversationRef.current.length > 0) {
            // AI messages are stored with their rendered HTML (including the feedback container)
            // so the MutationObserver can inject feedback buttons into the restored history.
            setInitialMessages(conversationRef.current.map(msg =>
                msg.html ? { role: msg.role, html: msg.html } : { role: msg.role, text: msg.text }
            ));
            setChatKey(k => k + 1);
            setIsOpen(true);
            return;
        }

        // First open: fetch previous-session history from the server
        if (!historyFetchedRef.current) {
            fetch('/api/chat/history')
                .then(r => r.json())
                .then(data => {
                    historyFetchedRef.current = true;
                    if (data.messages?.length > 0 && conversationRef.current.length === 0) {
                        const msgs = data.messages.map(m => {
                            if (m.role === 'ai' && m.messageId && m.sessionId) {
                                const renderedMarkdown = renderAssistantMarkdown(m.message);
                                // Re-inject the reschedule buttons if this turn offered slots, so they
                                // survive a refresh. The newest message's slots stay tappable; older
                                // ones are disabled by the feedback-render hide logic as each renders.
                                const rescheduleHtml = buildRescheduleHtml(m.rescheduleOptions);
                                const html = `<div>${renderedMarkdown}${rescheduleHtml}<div class="chat-feedback-container" data-message-id="${m.messageId}" data-session-id="${m.sessionId}" style="display:flex;justify-content:flex-end;margin-top:4px;pointer-events:auto;position:relative;z-index:1;"></div></div>`;
                                if (m.reaction) {
                                    reactionsRef.current[m.messageId] = m.reaction;
                                }
                                return { role: 'ai', text: m.message, html };
                            }
                            return { role: m.role, text: m.message, html: null };
                        });
                        conversationRef.current = msgs;
                        setInitialMessages(msgs.map(m => m.html ? { role: m.role, html: m.html } : { role: m.role, text: m.text }));
                        setChatKey(k => k + 1);
                    }
                })
                .catch(err => {
                    historyFetchedRef.current = false;
                    console.error('Failed to load chat history:', err);
                });
        }
        setIsOpen(true);
    };

    const connect = useMemo(() => ({
        url: '/api/chat/message',
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'XSRF-TOKEN': xsrfToken
        }
    }), [xsrfToken]);

    const requestInterceptor = useCallback((requestDetails) => {
        const userMessage = requestDetails.body.messages[requestDetails.body.messages.length - 1].text;
        requestDetails.body = { message: userMessage };
        conversationRef.current = [...conversationRef.current, { role: 'user', text: userMessage, html: null }];
        return requestDetails;
    }, []);

    const responseInterceptor = useCallback((response) => {
        if (response.success && response.response) {
            if (response.sessionId) {
                sessionIdRef.current = response.sessionId;
            }
            if (response.messageId) {
                messageMetaRef.current.push({
                    messageId: response.messageId,
                    sessionId: response.sessionId
                });
            }
            const renderedMarkdown = renderAssistantMarkdown(response.response);
            // When the turn offered reschedule slots, embed a container the MutationObserver fills
            // with clickable booking buttons. The slot payload (incl. each remoteMeetingId) rides in
            // a data attribute so a click can post the id directly, never re-derived from chat text.
            const rescheduleHtml = buildRescheduleHtml(response.rescheduleOptions);
            const renderedHtml = `<div>${renderedMarkdown}${rescheduleHtml}<div class="chat-feedback-container" data-message-id="${response.messageId}" data-session-id="${response.sessionId}" style="display:flex;justify-content:flex-end;margin-top:4px;pointer-events:auto;position:relative;z-index:1;"></div></div>`;
            // Store the full rendered HTML so feedback buttons can be restored when the
            // chat is reopened and messages are reloaded as history.
            conversationRef.current = [...conversationRef.current, { role: 'ai', html: renderedHtml }];
            return { html: renderedHtml };
        } else if (response.error) {
            conversationRef.current = [...conversationRef.current, { role: 'ai', text: response.error, html: null }];
            return { text: response.error };
        }
        const fallback = "I apologize, but I couldn't process your request. Please try again.";
        conversationRef.current = [...conversationRef.current, { role: 'ai', text: fallback, html: null }];
        return { text: fallback };
    }, []);

    const onComponentRender = useCallback((chatEl) => {
        // Clean up any previous observer (fires with null when DeepChat unmounts/remounts)
        if (observerCleanupRef.current) {
            observerCleanupRef.current();
            observerCleanupRef.current = null;
        }
        if (!chatEl) {
            latestFeedbackHideRef.current = null;
            return;
        }

        const observer = new MutationObserver(() => {
            const containers = chatEl.shadowRoot
                ? chatEl.shadowRoot.querySelectorAll('.chat-feedback-container:not([data-rendered])')
                : chatEl.querySelectorAll('.chat-feedback-container:not([data-rendered])');

            containers.forEach(container => {
                container.setAttribute('data-rendered', 'true');
                const msgId = parseInt(container.getAttribute('data-message-id'));
                const sessId = container.getAttribute('data-session-id');
                if (msgId && sessId) {
                    renderFeedbackButtons(container, sessId, msgId, xsrfToken, latestFeedbackHideRef, reactionsRef);
                }
            });

            const slotContainers = chatEl.shadowRoot
                ? chatEl.shadowRoot.querySelectorAll('.chat-reschedule-container:not([data-rendered])')
                : chatEl.querySelectorAll('.chat-reschedule-container:not([data-rendered])');

            slotContainers.forEach(container => {
                container.setAttribute('data-rendered', 'true');
                const raw = container.getAttribute('data-options');
                if (!raw) return;
                try {
                    renderSlotButtons(container, JSON.parse(decodeURIComponent(raw)), xsrfToken);
                } catch (err) {
                    console.error('Failed to render reschedule slots:', err);
                }
            });
        });

        const target = chatEl.shadowRoot || chatEl;
        observer.observe(target, { childList: true, subtree: true });
        observerCleanupRef.current = () => observer.disconnect();
    }, [xsrfToken]);

    return (
        <>
            {isOpen && (
                <div style={CHAT_PANEL_STYLE}>
                    <DeepChat
                        key={chatKey}
                        ref={onComponentRender}
                        style={DEEP_CHAT_STYLE}
                        introMessage={!initialMessages ? introMessage : undefined}
                        history={initialMessages}
                        connect={connect}
                        requestInterceptor={requestInterceptor}
                        responseInterceptor={responseInterceptor}
                        htmlClassUtilities={HTML_CLASS_UTILITIES}
                        messageStyles={MESSAGE_STYLES}
                        inputAreaStyle={INPUT_AREA_STYLE}
                        submitButtonStyles={SUBMIT_BUTTON_STYLES}
                        avatars={AVATARS}
                        textInput={TEXT_INPUT}
                    />
                </div>
            )}
            <button
                style={TOGGLE_BUTTON_STYLE}
                onClick={toggleChat}
                onMouseEnter={onToggleButtonMouseEnter}
                onMouseLeave={onToggleButtonMouseLeave}
                title="Chat with JC Assistant"
            >
                {isOpen ? (
                    <span style={ICON_STYLE}>&#x2715;</span>
                ) : (
                    <span style={ICON_STYLE}>&#x1F4AC;</span>
                )}
            </button>
        </>
    );
};

export default ChatAgentComponent;
